import * as React from 'react';
import { action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { addDays, subDays } from 'date-fns';
import { cs } from '@shared/utils/utils-classname';
import { DateUtils, IDatePreset } from '@shared/utils/DateUtils';
import './DateRangePicker.scss';

import 'react-datepicker/dist/react-datepicker.css';
import { DateTimePicker } from '@shared/ui-react/DateTimePicker';
import { Catch } from '@shared/ui-react/Catch';


@observer
export class DateRangePicker extends React.Component<{
    store: DateRange.IFilter
    onChange: (from, to, presetIndex) => void;
    visiblePresetsCount?: number;
    mobile?: boolean;
    clearable?: boolean;
}> {
    static defaultProps = {
        visiblePresetsCount: 4,
        mobile: false,
        clearable: false,
    };

    constructor(
        props: any
    ) {
        super(props);
        makeObservable(this);
    }

    render() {
        const from = this.props.store.fromTimestamp;
        const to = this.props.store.toTimestamp;
        const hasAny = !!from || !!to;
        const isMobile = this.props.mobile;
        const clearable = this.props.clearable;
        const activePreset = this.props.store.datePresetIndex != null ? DateUtils.presets.find(p => p.index === this.props.store.datePresetIndex) : null;

        return (
            <Catch showInline text='Datepicker error' sendReport={false}>
                <span className={cs('datepicker-wrapper', { mobile: isMobile })}>
                    <Presets activePreset={activePreset} onSelected={this.activatePreset} visiblePresetsCount={this.props.visiblePresetsCount} />
                    <span className='datepicker-inputs'>
                        <span className='datepicker-from'>{kuura.getMessage('general.from')}:</span>
                        <DateTimePicker value={from} onChange={this.onFromChange} mobile={isMobile} clearable={clearable} selectsStart startDate={from} endDate={to} maxDate={to}/>

                        <span className='datepicker-to'>{kuura.getMessage('general.to')}:</span>
                        <DateTimePicker value={to} onChange={this.onToChange} mobile={isMobile} clearable={clearable} selectsEnd startDate={from} endDate={to} minDate={from}/>

                        <span className="btn-group" style={{ marginLeft: 10 }}>
                            <button className='ik-button-small' title='Subtract day' onClick={this.dayMinus} disabled={!hasAny} style={{ height: 26, width: this.props.mobile ? 40 : 'initial' }}>-</button>
                            <button className='ik-button-small' title='Add day' onClick={this.dayPlus} disabled={!hasAny} style={{ height: 26, width: this.props.mobile ? 40 : 'initial' }}>+</button>
                        </span>
                    </span>
                </span>
            </Catch>
        );
    }

    @action.bound
    activatePreset(preset: IDatePreset) {
        if (!preset) return;
        this.setDates(DateUtils.presets[preset.index].from, DateUtils.presets[preset.index].to, preset.index);
    }

    @action.bound
    setDates(from: number, to: number, presetIndex: number) {
        if (from) {
            from = +moment(from).startOf('day');
        }
        if (to) {
            to = +moment(to).endOf('day');
        }
        this.props.onChange(from, to, presetIndex);
    }

    onFromChange = date => {
        if (date) {
            this.setDates(+moment(date), this.props.store.toTimestamp, null);
        }
        else {
            this.setDates(null, this.props.store.toTimestamp, null);
        }
    };

    onToChange = date => {
        if (date) {
            this.setDates(this.props.store.fromTimestamp, +moment(date), null);
        }
        else {
            this.setDates(this.props.store.fromTimestamp, null, null);
        }
    };

    dayPlus = () => {
        let newFrom = this.props.store.fromTimestamp;
        let newTo = this.props.store.toTimestamp;
        if (this.props.store.fromTimestamp) {
            newFrom = +addDays(this.props.store.fromTimestamp, 1);
        }
        if (this.props.store.toTimestamp) {
            newTo = +addDays(this.props.store.toTimestamp, 1);
        }
        this.setDates(newFrom, newTo, null);
    }

    dayMinus = () => {
        let newFrom = this.props.store.fromTimestamp;
        let newTo = this.props.store.toTimestamp;
        if (this.props.store.fromTimestamp) {
            newFrom = +subDays(this.props.store.fromTimestamp, 1);
        }
        if (this.props.store.toTimestamp) {
            newTo = +subDays(this.props.store.toTimestamp, 1);
        }
        this.setDates(newFrom, newTo, null);
    }
}

@observer
class Presets extends React.Component<{
    activePreset: IDatePreset;
    onSelected: (preset: IDatePreset) => void
    visiblePresetsCount: number;
}> {
    readonly visiblePresets = DateUtils.presets.slice(0, this.props.visiblePresetsCount);
    readonly overflowPresets = DateUtils.presets.slice(this.props.visiblePresetsCount);

    state = {
        isMoreMenuOpen: false,
    };

    triggerRef = React.createRef<HTMLSpanElement>();

    handleMouseEnter = () => {
        if (this.triggerRef.current) {
            this.setState({ isMoreMenuOpen: true });
        }
    };

    handleMouseLeave = () => {
        this.setState({ isMoreMenuOpen: false });
    };

    render() {
        const { activePreset, onSelected } = this.props;
        const { isMoreMenuOpen } = this.state;

        return (
            <span className='datepicker-presets'>
                { this.visiblePresets.map(p =>
                    (<span
                        onClick={() => onSelected(p)}
                        key={p.index}
                        className={cs('datepicker-preset', { active: activePreset && activePreset.index === p.index })}>
                        {p.label}
                      </span>)
                )}

                { this.overflowPresets.length > 0 && (
                    <div
                        onMouseEnter={this.handleMouseEnter}
                        onMouseLeave={this.handleMouseLeave}
                        style={{ display: 'inline-block', position: 'relative' }}
                    >
                        <span
                            className='datepicker-more-trigger'
                            ref={this.triggerRef}>
                            ... <i className='iconf-caret'/>
                        </span>

                        {isMoreMenuOpen && (
                            <div className='datepicker-more-menu' style={{ position: 'fixed', zIndex: 10001  }}>
                                {this.overflowPresets.map(p =>
                                    <div className='datepicker-preset' onClick={() => onSelected(p)} key={p.index}>{p.label}</div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </span>
        );
    }
}
